import { ANDROID_WRAPPER_EVENTS } from '@/event-manager/types';
import {
  closeLauncherShell,
  isEmbeddedShell,
} from '@/logic/web-wrapper/launcher-embedded-navigation';
import { sendAndroidWrapperEvent } from './wrapperHelpers';
import config from '@/config';
import { PLATFORMS } from '@/utilities/constants/game';
import { useStore } from '@/store/useStore';
import { SAME_ORIGIN_GAME_WINDOW_PLAY_PATH } from '@/utilities/helpers/same-origin-path.ts';

/**
 * Mobile web launch uses `_self`, so iOS Safari often keeps the pre-game lobby in BFCache.
 * Redirecting to the same lobby URL can restore that frozen document (React effects /
 * Account UM never re-run) while cookies changed in-game — lobby looks logged-in but hangs.
 * A unique hash (not sent to the server) forces a fresh load without casino-next changes.
 */
export const buildMobileWebLobbyExitUrl = (lobbyUrl: string): string => {
  try {
    const url = new URL(lobbyUrl, window.location.origin);
    url.hash = `_gwExit=${Date.now().toString(36)}`;
    return url.href;
  } catch {
    return lobbyUrl;
  }
};

/** Top-level navigation so the lobby is not loaded inside a game iframe (iOS mobile web). */
const navigateMobileWebToLobby = (lobbyUrl: string): void => {
  const navigationTarget = window.top ?? window;
  navigationTarget.location.replace(buildMobileWebLobbyExitUrl(lobbyUrl));
};

export const exitGame = (url?: string) => {
  if (isEmbeddedShell()) {
    void closeLauncherShell();
    return;
  }

  const { wrapperType, platform } = useStore.getState().runtime;
  const isDevstack = window.origin.includes('devstack');

  if (wrapperType.isMobile) {
    sendAndroidWrapperEvent(ANDROID_WRAPPER_EVENTS.GAME_CLOSE, url || '');
    return;
  }

  let lobbyUrl = config.lobbyRedirectUrl || config.casinoWebUrl;
  if (isDevstack) {
    if (window.location.pathname.includes(SAME_ORIGIN_GAME_WINDOW_PLAY_PATH)) {
      lobbyUrl = window.location.host;
    } else {
      lobbyUrl = `https://${window.location.hostname.replace('gw.', '')}`;
    }
  }

  if (platform === PLATFORMS.MobileWeb) {
    navigateMobileWebToLobby(lobbyUrl);
    return;
  }
  window.close();
  window.location.href = lobbyUrl;
};
